import { useAtom } from 'jotai'
import { atomWithStorage } from 'jotai/utils'
import { useEffect } from 'react'
import { usePermission } from 'react-use'
import { IState } from 'react-use/lib/usePermission'
import useMediaDevices from '~/hooks/useMediaDevices'
import useSetSinkIdPolyfill from '~/hooks/useSetSinkIdPolyfill'
import Logger from '~/services/Logger'

import { StorageKeys } from './StorageKeys'

const logger = new Logger('AudioSettings')

interface AudioPreferences {
  inputDeviceId: string
  outputDeviceId: string
}
const audioPreferencesAtom = atomWithStorage<AudioPreferences>(
  StorageKeys.AudioDevicePreferences,
  {
    inputDeviceId: 'default',
    outputDeviceId: 'default',
  },
)

interface UseAudioSettingsStateResponse {
  permission: IState
  output: {
    devices: MediaDeviceInfo[]
    preference: [string, (deviceId: string) => void]
  }
  input: {
    devices: MediaDeviceInfo[]
    preference: [string, (deviceId: string) => void]
  }
}
const useAudioSettingsState = (): UseAudioSettingsStateResponse => {
  const [preferences, setPreferences] = useAtom(audioPreferencesAtom)
  const setInputPreference = (deviceId: string) => {
    setPreferences({
      ...preferences,
      inputDeviceId: deviceId,
    })
  }
  const setOutputPreference = (deviceId: string) => {
    setPreferences({
      ...preferences,
      outputDeviceId: deviceId,
    })
  }
  const microphonePermissionState = usePermission({ name: 'microphone' })

  // This polyfill is required for Firefox <116 and sets the setSinkId method to a noop.
  useSetSinkIdPolyfill()

  const { mediaDevices: devices, requeryDevices: requeryMediaDevices } =
    useMediaDevices()

  // Check microphone permission state and prompt if necessary.
  // Depends on changes to the tracked microphone permission state.
  useEffect(() => {
    const checkAudioAccess = () => {
      try {
        requeryMediaDevices()
      } catch {
        logger.error(
          'Audio access denied',
          new Error('Microphone access prompt was explicity denied.'),
        )
      }
    }

    switch (microphonePermissionState) {
      case 'granted':
        logger.debug('Permission granted')
        break
      case 'denied':
        logger.debug('Permission denied')
        break
      case 'prompt':
        logger.debug('Permission prompt')
        checkAudioAccess()
        break
      default:
        logger.debug(`Permission ${microphonePermissionState}`)
        checkAudioAccess()
        break
    }
  }, [microphonePermissionState])

  // Ensure the preferred audio input and output devices are available.
  // Depends on changes to the navigator.mediaDevices list of devices.
  useEffect(() => {
    const preferedInputId = preferences.inputDeviceId
    const preferedOutputId = preferences.outputDeviceId
    if (
      !devices.audioInputs.find((md) => md.deviceId === preferedInputId) &&
      devices.audioInputs.length > 0
    ) {
      setInputPreference(devices.audioInputs[0]?.deviceId ?? 'default')
    }
    if (
      !devices.audioOutputs.find((md) => md.deviceId === preferedOutputId) &&
      devices.audioOutputs.length > 0
    ) {
      setOutputPreference(devices.audioOutputs[0]?.deviceId ?? 'default')
    }
  }, [devices, preferences])

  return {
    permission: microphonePermissionState,
    output: {
      devices: devices.audioOutputs,
      preference: [preferences.outputDeviceId, setOutputPreference],
    },
    input: {
      devices: devices.audioInputs,
      preference: [preferences.inputDeviceId, setInputPreference],
    },
  }
}

export { useAudioSettingsState }
